import React, { useEffect, useRef } from 'react';

import { BarChart } from 'echarts/charts';
import { GridComponent, LegendComponent, TooltipComponent } from 'echarts/components';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import dayjs from '~/timeInit';

import { Box, Paper, Typography } from '@mui/material';

import type { PivotRow } from './PivotTable';

echarts.use([BarChart, GridComponent, LegendComponent, TooltipComponent, CanvasRenderer]);

const millions = (value: number | null) => (value === null ? '' : `$${(value / 1e6).toFixed(2)}M`);
const PAYMENT_COLORS = ['#8fd3c1', '#8fd3c1', '#2b8a9e', '#2b8a9e'];

export default function SnapshotCharts({ rows, weekly = false }: { rows: Array<PivotRow>; weekly?: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  const title = weekly ? 'FYTD weekly sales and margin' : 'MTD cash and credit sales and refunds';
  const categories = weekly
    ? rows.map((row) => dayjs(row.label).format('D MMM YY'))
    : ['Cash sales', 'Credit sales', 'Cash refunds', 'Credit refunds'];
  const series = weekly
    ? [
        { name: 'Gross Sales (BR)', color: '#d4d4d4', data: rows.map((row) => row.values.sales) },
        { name: 'Gross Margin (AR)', color: '#1f3d99', data: rows.map((row) => row.values.margin) },
      ]
    : [
        {
          name: 'AUD',
          color: '#0F464F',
          data: ['cashSales', 'creditSales', 'cashRefunds', 'creditRefunds'].map((key) => {
            const values = rows.map((row) => row.values[key]).filter((value): value is number => value !== null);
            return values.length ? values.reduce((sum, value) => sum + value, 0) : null;
          }),
        },
      ];
  const identity = JSON.stringify({ categories, series });
  useEffect(() => {
    if (!ref.current) {
      return;
    }
    const { categories, series } = JSON.parse(identity);
    const chart = echarts.init(ref.current);
    chart.setOption({
      animation: false,
      tooltip: {
        trigger: 'axis',
        confine: true,
        valueFormatter: (value: number) =>
          value === null
            ? 'Unknown'
            : new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 }).format(
                value,
              ),
      },
      legend: { show: weekly, top: 0 },
      grid: { left: weekly ? 20 : 110, right: weekly ? 20 : 80, top: weekly ? 45 : 15, bottom: 30 },
      xAxis: weekly
        ? { type: 'category', data: categories, axisTick: { show: false } }
        : { type: 'value', axisLabel: { formatter: (value: number) => `${value / 1e6}M` } },
      yAxis: weekly ? { type: 'value', show: false } : { type: 'category', inverse: true, data: categories },
      series: series.map((item: { name: string; color: string; data: Array<number | null> }) => ({
        name: item.name,
        type: 'bar',
        data: weekly
          ? item.data
          : item.data.map((value, index) => ({ value, itemStyle: { color: PAYMENT_COLORS[index] } })),
        itemStyle: { color: item.color },
        ...(weekly ? { barGap: '-100%', barCategoryGap: '4%' } : {}),
        label: weekly
          ? {
              show: true,
              position: 'top',
              fontSize: 11,
              formatter: (point: { value: number }) => millions(point.value),
            }
          : {
              show: true,
              position: 'right',
              formatter: (point: { value: number }) =>
                point.value?.toLocaleString('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 }),
            },
      })),
    });
    const resize = new ResizeObserver(() => chart.resize());
    resize.observe(ref.current);
    return () => {
      resize.disconnect();
      chart.dispose();
    };
  }, [identity, weekly]);
  return (
    <Paper data-lens-pdf-card variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Typography variant="h6" sx={{ fontWeight: 600 }}>
        {title}
      </Typography>
      <Box
        ref={ref}
        role="img"
        aria-label={`${title}. ${categories.map((name, index) => `${name}: ${series.map((item) => `${item.name} ${item.data[index] === null ? 'Unknown' : item.data[index]?.toLocaleString('en-AU', { maximumFractionDigits: 0 })}`).join(', ')}`).join('; ')}`}
        sx={{ height: weekly ? 330 : 210, width: '100%' }}
      />
    </Paper>
  );
}
